<script setup lang="ts">
import {
    BarChart3,
    BookOpen,
    CircleCheck,
    CircleDollarSign,
    ClipboardList,
    Code,
    Coins,
    CreditCard,
    Database,
    FileDown,
    FileText,
    Gauge,
    Plug,
    ShieldCheck,
    Sparkles,
    User,
    UserRound,
    Users
} from 'lucide-vue-next';
import type { Component } from 'vue';
import { computed, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import AiModelManagementSection from '@/components/settingsMenu/sections/AiModelManagementSection.vue';
import BillingSection from '@/components/settingsMenu/sections/BillingSection.vue';
import ConnectionsSection from '@/components/settingsMenu/sections/ConnectionsSection.vue';
import DevToolsSection from '@/components/settingsMenu/sections/DevToolsSection.vue';
import OrganizationPromptSection from '@/components/settingsMenu/sections/OrganizationPromptSection.vue';
import PlansSection from '@/components/settingsMenu/sections/PlansSection.vue';
import QuotaManagementSection from '@/components/settingsMenu/sections/QuotaManagementSection.vue';
import ReportsSection from '@/components/settingsMenu/sections/ReportsSection.vue';
import SkillsSection from '@/components/settingsMenu/sections/SkillsSection.vue';
import SlideEvalsSection from '@/components/settingsMenu/sections/SlideEvalsSection.vue';
import SlideExportsSection from '@/components/settingsMenu/sections/SlideExportsSection.vue';
import TenantAiModelSelectionSection from '@/components/settingsMenu/sections/TenantAiModelSelectionSection.vue';
import UserManagementSection from '@/components/settingsMenu/sections/UserManagementSection.vue';
import UserProfileSection from '@/components/settingsMenu/sections/UserProfileSection.vue';
import UserUsageSection from '@/components/settingsMenu/sections/UserUsageSection.vue';
import WorkspaceAnalyticsSection from '@/components/settingsMenu/sections/WorkspaceAnalyticsSection.vue';
import WorkspaceGroupsSection from '@/components/settingsMenu/sections/WorkspaceGroupsSection.vue';
import WorkspaceMembersSection from '@/components/settingsMenu/sections/WorkspaceMembersSection.vue';
import WorkspacePermissionsSection from '@/components/settingsMenu/sections/WorkspacePermissionsSection.vue';
import SettingsNestedPortalStack from '@/components/settingsMenu/SettingsNestedPortalStack.vue';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
} from '@/components/ui/sidebar';
import { getAiModels } from '@/services/ai_models';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useAuthStore } from '@/stores/auth';
import { useConfigCatStore } from '@/stores/configCat';
import { useTenantStore } from '@/stores/tenant';
import { SubscriptionPlan } from '@/types';

type SettingsTab =
    | 'user-profile'
    | 'usage'
    | 'workspace-groups'
    | 'workspace-members'
    | 'user-management'
    | 'organization-prompt'
    | 'workspace-permissions'
    | 'skills'
    | 'workspace-analytics'
    | 'connections'
    | 'tenant-model-selection'
    | 'model-catalog'
    | 'quotas'
    | 'plans'
    | 'billing'
    | 'dev-tools'
    | 'reports'
    | 'slide-exports'
    | 'slide-evals';

type MenuSection = {
    label: string;
    items: Array<{
        label: string;
        value: SettingsTab;
        icon: Component;
        visible: boolean;
    }>;
};

const tenantStore = useTenantStore();
const configCatStore = useConfigCatStore();
const auth = useAuthStore();
const accessContextStore = useAccessContextStore();

const isAdmin = computed(() => {
    return tenantStore.tenantAdmin;
});

const canManageUsers = computed(() => {
    return (
        configCatStore.userManagementTab &&
        isAdmin.value &&
        !auth.isInsideTeams &&
        auth.isDescopeEnabled
    );
});

const canAccessElqanoAdminSettings = computed(() => {
    return isAdmin.value && tenantStore.tenantElqano;
});

const hasAiModels = ref(false);

const loadAiModels = async () => {
    const tenantId = tenantStore.tenantId;
    if (!tenantStore.tenantAdmin) {
        hasAiModels.value = false;
        return;
    }
    if (!tenantId) {
        hasAiModels.value = false;
        return;
    }
    try {
        const models = await getAiModels(tenantId);
        hasAiModels.value = models.length > 0;
    } catch {
        hasAiModels.value = false;
    }
};

watch(() => [tenantStore.tenantId, tenantStore.tenantAdmin], loadAiModels, {
    immediate: true
});

provide('refreshHasAiModels', loadAiModels);

const canAccessBillingSettings = computed(() => {
    return isAdmin.value && accessContextStore.billingProvider !== 'direct';
});

const isOpen = defineModel<boolean>('open', { default: false });
const currentTab = ref<SettingsTab>('user-profile');

const currentSubscriptionPlan = ref<SubscriptionPlan>(SubscriptionPlan.PRO);

const goToTab = (tab: SettingsTab) => {
    currentTab.value = tab;
};

const menuSections = computed<MenuSection[]>(() => {
    return [
        {
            label: 'Account',
            items: [
                {
                    label: 'User profile',
                    value: 'user-profile',
                    icon: UserRound,
                    visible: true
                },
                {
                    label: 'Usage',
                    value: 'usage',
                    icon: Gauge,
                    visible: auth.isDescopeEnabled
                }
            ]
        },
        {
            label: 'Workspace',
            items: [
                {
                    label: 'User management',
                    value: 'user-management',
                    icon: User,
                    visible: canManageUsers.value
                },
                {
                    label: 'Members',
                    value: 'workspace-members',
                    icon: User,
                    visible: false
                },
                {
                    label: 'Groups',
                    value: 'workspace-groups',
                    icon: Users,
                    visible: isAdmin.value
                },
                {
                    label: 'Organization prompt',
                    value: 'organization-prompt',
                    icon: FileText,
                    visible: isAdmin.value
                },
                {
                    // Visible to everyone: a member cannot import, but each one decides which of
                    // the organisation's procedures they want in their own conversations.
                    label: 'Skills',
                    value: 'skills',
                    icon: BookOpen,
                    visible: true
                },
                {
                    label: 'Permissions',
                    value: 'workspace-permissions',
                    icon: ShieldCheck,
                    visible: isAdmin.value
                },
                {
                    label: 'AI Models',
                    value: 'tenant-model-selection',
                    icon: Sparkles,
                    visible: isAdmin.value && hasAiModels.value
                },
                {
                    label: 'Analytics',
                    value: 'workspace-analytics',
                    icon: BarChart3,
                    visible: isAdmin.value
                }
            ]
        },
        {
            label: 'Integrations',
            items: [
                {
                    // Visible to everyone: MCP connectors are per-user, and which ones
                    // a user may connect is already decided by their ConfigCat flag.
                    label: 'Connections',
                    value: 'connections',
                    icon: Plug,
                    visible: true
                }
            ]
        },
        {
            label: 'Access / billing',
            items: [
                {
                    label: 'Billing',
                    value: 'billing',
                    icon: CreditCard,
                    visible: canAccessBillingSettings.value
                },
                {
                    label: 'Plans',
                    value: 'plans',
                    icon: CircleDollarSign,
                    visible: canAccessBillingSettings.value
                }
            ]
        },
        {
            label: 'Elqano only',
            items: [
                {
                    label: 'Dev tools',
                    value: 'dev-tools',
                    icon: Code,
                    visible: canAccessElqanoAdminSettings.value
                },
                {
                    label: 'Model Catalog',
                    value: 'model-catalog',
                    icon: Database,
                    visible: canAccessElqanoAdminSettings.value
                },
                {
                    label: 'Reports',
                    value: 'reports',
                    icon: ClipboardList,
                    visible: canAccessElqanoAdminSettings.value
                },
                {
                    label: 'Quotas',
                    value: 'quotas',
                    icon: Coins,
                    visible: canAccessElqanoAdminSettings.value
                },
                {
                    label: 'Slide exports',
                    value: 'slide-exports',
                    icon: FileDown,
                    visible: canAccessElqanoAdminSettings.value
                },
                {
                    label: 'Quality score',
                    value: 'slide-evals',
                    icon: CircleCheck,
                    visible: canAccessElqanoAdminSettings.value
                }
            ]
        }
    ];
});

const filteredMenuSections = computed(() => {
    return menuSections.value
        .map((section) => {
            return {
                ...section,
                items: section.items.filter((item) => item.visible)
            };
        })
        .filter((section) => {
            return section.items.length > 0;
        });
});

const visibleTabs = computed(() => {
    return filteredMenuSections.value.flatMap((section) => {
        return section.items.map((item) => item.value);
    });
});

const pendingTab = ref<string | null>(null);

watch(visibleTabs, (tabs) => {
    if (pendingTab.value && tabs.includes(pendingTab.value as SettingsTab)) {
        currentTab.value = pendingTab.value as SettingsTab;
        isOpen.value = true;
        pendingTab.value = null;
    } else if (!tabs.includes(currentTab.value)) {
        currentTab.value = 'user-profile';
    }
});

watch(isOpen, (open) => {
    if (!open) {
        currentTab.value = 'user-profile';
        pendingTab.value = null;
    }
});

const route = useRoute();
const router = useRouter();

watch(
    () => route.query.settingsTab,
    (tab) => {
        if (tab && typeof tab === 'string') {
            if (visibleTabs.value.includes(tab as SettingsTab)) {
                currentTab.value = tab as SettingsTab;
                isOpen.value = true;
            } else {
                pendingTab.value = tab;
            }
        } else {
            pendingTab.value = null;
        }
    },
    { immediate: true }
);

watch(isOpen, (open) => {
    if (!open && route.query.settingsTab) {
        const rest = { ...route.query };
        delete rest.settingsTab;
        router.replace({ query: rest });
    }
});
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogContent
            class="w-[95vw] max-w-6xl h-[85vh] overflow-hidden p-0 flex flex-col z-[60]"
        >
            <DialogHeader class="sr-only">
                <DialogTitle>Settings</DialogTitle>

                <DialogDescription>
                    Manage account, workspace, integrations and billing
                    settings.
                </DialogDescription>
            </DialogHeader>

            <SettingsNestedPortalStack>
                <SidebarProvider class="h-full w-full flex-1 min-h-0">
                    <Sidebar
                        collapsible="none"
                        class="w-60 shrink-0 border-r bg-primary10"
                    >
                        <SidebarContent class="gap-0 p-2">
                            <SidebarGroup
                                v-for="section in filteredMenuSections"
                                :key="section.label"
                                class="px-2 py-2"
                            >
                                <SidebarGroupLabel
                                    class="p-1 text-[.6rem] uppercase font-medium tracking-wide text-muted-foreground"
                                >
                                    {{ section.label }}
                                </SidebarGroupLabel>
                                <SidebarMenu class="m-0 list-none p-0">
                                    <SidebarMenuItem
                                        v-for="item in section.items"
                                        :key="item.value"
                                        class="w-full list-none"
                                    >
                                        <SidebarMenuButton
                                            class="h-9 w-full text-foreground font-medium"
                                            :class="
                                                currentTab === item.value
                                                    ? 'bg-primary30 hover:bg-primary30'
                                                    : 'hover:bg-primary20'
                                            "
                                            :is-active="
                                                currentTab === item.value
                                            "
                                            @click="currentTab = item.value"
                                        >
                                            <component
                                                :is="item.icon"
                                                class="size-4 shrink-0"
                                            />
                                            <span>{{ item.label }}</span>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroup>
                        </SidebarContent>
                    </Sidebar>

                    <ScrollArea class="min-h-0 min-w-0 flex-1 h-full">
                        <div class="tab-content min-w-0 p-12">
                            <UserManagementSection
                                v-if="currentTab === 'user-management'"
                            />
                            <WorkspaceMembersSection
                                v-if="currentTab === 'workspace-members'"
                            />
                            <WorkspaceGroupsSection
                                v-else-if="currentTab === 'workspace-groups'"
                            />
                            <OrganizationPromptSection
                                v-else-if="currentTab === 'organization-prompt'"
                            />
                            <WorkspacePermissionsSection
                                v-else-if="
                                    currentTab === 'workspace-permissions'
                                "
                            />
                            <SkillsSection
                                v-else-if="currentTab === 'skills'"
                            />
                            <WorkspaceAnalyticsSection
                                v-else-if="currentTab === 'workspace-analytics'"
                            />
                            <ConnectionsSection
                                v-else-if="currentTab === 'connections'"
                            />
                            <TenantAiModelSelectionSection
                                v-else-if="
                                    currentTab === 'tenant-model-selection'
                                "
                            />
                            <AiModelManagementSection
                                v-else-if="currentTab === 'model-catalog'"
                            />
                            <QuotaManagementSection
                                v-else-if="currentTab === 'quotas'"
                            />
                            <PlansSection
                                v-else-if="currentTab === 'plans'"
                                :current-subscription-plan="
                                    currentSubscriptionPlan
                                "
                                :is-trial="
                                    accessContextStore.subscriptionStatus ===
                                    'trialing'
                                "
                                :is-grace-period="
                                    accessContextStore.gracePeriodDaysRemaining !==
                                        null &&
                                    accessContextStore.gracePeriodDaysRemaining >
                                        0
                                "
                            />
                            <BillingSection
                                v-else-if="currentTab === 'billing'"
                                :current-subscription-plan="
                                    currentSubscriptionPlan
                                "
                                @go-to-tab="goToTab"
                            />
                            <UserProfileSection
                                v-else-if="currentTab === 'user-profile'"
                            />
                            <UserUsageSection
                                v-else-if="currentTab === 'usage'"
                            />
                            <DevToolsSection
                                v-else-if="currentTab === 'dev-tools'"
                            />
                            <ReportsSection
                                v-else-if="currentTab === 'reports'"
                            />
                            <SlideExportsSection
                                v-else-if="currentTab === 'slide-exports'"
                            />
                            <SlideEvalsSection
                                v-else-if="currentTab === 'slide-evals'"
                            />
                        </div>
                    </ScrollArea>
                </SidebarProvider>
            </SettingsNestedPortalStack>
        </DialogContent>
    </Dialog>
</template>
